import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle } from 'react-feather';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signInWithGoogle } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      await login(email, password);
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      console.log('Starting Google sign-in...');
      await signInWithGoogle();
      console.log('Google sign-in successful!');
    } catch (err) {
      console.error('Google sign-in error:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to sign in with Google';
      
      if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in popup was closed. Please try again.';
      } else if (err.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (err.code === 'auth/popup-blocked') {
        errorMessage = 'Pop-up was blocked by your browser. Please allow pop-ups for this site.';
      } else if (err.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized for Google sign-in. Please contact support.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div 
          style={{
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
            borderRadius: '12px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#dc2626',
            marginBottom: '24px',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
          }}
        >
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Google Sign In Button */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={loading}
        style={{
          width: '100%',
          padding: '14px 24px',
          border: '1.5px solid #e5e5e5',
          background: 'white',
          borderRadius: '12px',
          fontSize: '16px',
          fontWeight: 500,
          color: '#1a1a1a',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '32px',
          transition: 'all 0.2s',
          fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
        }}
        onMouseEnter={(e) => {
          if (!loading) {
            e.target.style.borderColor = '#d4d4d4';
            e.target.style.background = '#fafafa';
          }
        }}
        onMouseLeave={(e) => {
          if (!loading) {
            e.target.style.borderColor = '#e5e5e5';
            e.target.style.background = 'white';
          }
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </button>

      {/* Email Field */}
      <div style={{ marginBottom: '24px' }}>
        <label
          htmlFor="email"
          style={{
            display: 'block',
            fontSize: '15px',
            fontWeight: 500,
            color: '#404040',
            marginBottom: '8px',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
          }}
        >
          Email
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="enter email..."
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px 16px',
            border: '1.5px solid #e5e5e5',
            borderRadius: '12px',
            fontSize: '15px',
            color: '#1a1a1a',
            transition: 'all 0.2s',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
            fontStyle: 'normal',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.outline = 'none';
            e.target.style.borderColor = '#5b4cdb';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e5e5e5';
          }}
        />
      </div>

      {/* Password Field */}
      <div style={{ marginBottom: '24px' }}>
        <label
          htmlFor="password"
          style={{
            display: 'block',
            fontSize: '15px',
            fontWeight: 500,
            color: '#404040',
            marginBottom: '8px',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
          }}
        >
          Password
        </label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder="enter password..."
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px 16px',
            border: '1.5px solid #e5e5e5',
            borderRadius: '12px',
            fontSize: '15px',
            color: '#1a1a1a',
            transition: 'all 0.2s',
            fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
            fontStyle: 'normal',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.outline = 'none';
            e.target.style.borderColor = '#5b4cdb';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e5e5e5';
          }}
        />
      </div>

      {/* Sign In Button */}
      <button
        type="submit"
        disabled={loading}
        style={{
          width: '100%',
          padding: '16px 24px',
          background: '#5432FF', // primary-500
          border: 'none',
          borderRadius: '12px',
          fontSize: '16px',
          fontWeight: 600,
          color: 'white',
          cursor: 'pointer',
          marginTop: '32px',
          transition: 'all 0.2s',
          fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
        }}
        onMouseEnter={(e) => {
          if (!loading) {
            e.target.style.background = '#4328cc'; // primary-600
          }
        }}
        onMouseLeave={(e) => {
          if (!loading) {
            e.target.style.background = '#5432FF'; // primary-500
          }
        }}
      >
        {loading ? 'Signing In...' : 'Sign In'}
      </button>
    </form>
  );
};

export default SignIn;
